import { computed, type ComputedRef } from 'vue';
import type { Permission } from '../api/permissions.api.types';
import { usePermissionsStore } from '../store/permissions.store';

/**
 * Permission gating primitives for templates and script setup.
 *
 * Returns `ComputedRef<boolean>` so checks placed in `<script setup>`
 * (e.g. `const canEdit = has('...')`) stay reactive — not snapshots.
 *
 * Empty-array inputs to `hasAll` / `hasAny` evaluate to `false`
 * (failsafe-deny), matching `<PermissionGuard need=[]>` behavior.
 *
 * `isReady` flips to `true` once `SESSION_INIT` has hydrated the
 * permissions store (`lastFetchedAt !== null`). Consumers can use it to
 * render a skeleton during the hydration window instead of the
 * fallback slot, which otherwise renders for a short false-negative.
 *
 * Feature-flag gate lives once, inside
 * `usePermissionsStore().hasPermission` — neither the composable nor
 * `<PermissionGuard>` touches GrowthBook directly.
 */
export function usePermission() {
  const store = usePermissionsStore();

  const has = (key: Permission): ComputedRef<boolean> =>
    computed(() => store.hasPermission(key));

  const hasAll = (keys: Permission[]): ComputedRef<boolean> =>
    computed(
      () => keys.length > 0 && keys.every((k) => store.hasPermission(k)),
    );

  const hasAny = (keys: Permission[]): ComputedRef<boolean> =>
    computed(() => keys.length > 0 && keys.some((k) => store.hasPermission(k)));

  const isReady = computed(() => store.lastFetchedAt !== null);

  return { has, hasAll, hasAny, isReady };
}
